import React, { Component } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import CONTENT_CONTROLLER from '../../controllers/content';
import LANGUAGES_CONTROLLER from '../../controllers/languages';
import COMPANIES_CONTROLLER from '../../controllers/companies';
import getClientConfig from '../../utils/getClientConfig';

// Add and manage data
import Upload from '../upload/Page';
import ProcessedDocuments from '../processedDocuments/Page';
import UploadedDocuments from '../uploadedDocuments/Page';
import NewUploadPage from '../newUpload/Page';
import TemplatesPage from '../templates/Page';

// Errors and warnings
import ErrorsAndWarnings from '../errorsWarnings/Page';
import ErrorDocumentDetail from '../errorDocumentDetail/Page';

// Check and view
import OpenReturns from '../openReturns/Page';
import FilingPage from '../filing/FilingPage';
import ClosedReturns from '../closedReturns/Page';

// Tools
import VATCodeSimulator from '../vatCodeSimulator/Page';
import VATCodes from '../vatCodes/Page';
import TransactionReports from '../dataMining/Page';
import VATCodeReport from '../vatCodeReport/Page';
import MergeXmlFiles from '../mergeXmlFiles/Page';
import CountriesOnHold from '../countriesOnHold/Page'
import SalesAndPurchaseLedger from '../salesAndPurchaseLedger/Page';

// Transaction Details
import TransactionSearch from '../transactionSearch/Page';
import TransactionEdit from '../transactionEdit/Page';
import BulkActions from '../bulkActions/Page';

// Account
import AccountSettings from '../accountSettings/Page';
import CompanyPermissionsPage from '../companyPermissions/Page';
import ViewCompanyPermissions from '../companyPermissions/ViewCompanyPermissions';
import EditCompanyPermissions from '../companyPermissions/EditCompanyPermissions';
import DirectSubmissionPermissionsPage from '../directSubmissionPermissions/Page';
import UserManagementPage from '../userManagement/Page';
import ViewUserDetails from '../userManagement/ViewUserDetails';
import AddUserRouteGuard from '../userManagement/AddUserRouteGuard';
import EditUser from '../userManagement/EditUser';
import VATGroups from '../vatGroups/Page';
import Companies from '../companies/Page';

//Live Reporting
import LiveReportingSetup from '../liveReportingSetup/Page';
import ViewTransmissions from '../viewTransmissions/Page';

//Dynamic Reports
import DynamicReportForm from '../dynamicReports/DynamicReportForm';

//Settings
import ExchangeRates from '../exchangeRates/Page';
import ApiLicenseKeyPage from '../apiLicenseKey/Page';

// Other
import NotFound from '../page/NotFound';
import NotAuthorized from '../page/NotAuthorized';

//Products
import Products from '../authorization/Products';
import AuthorizedRoute from '../authorization/AuthorizedRoute';
import { FeatureFlagEnum, ProductEnum } from '../../utils/enums';
import AUTHENTICATION_API from "../../controllers/authentication";

import Logger, { LOG_LEVELS } from '../../utils/logger';
import BusySpinner from '../spinners/BusySpinner';
import ManualDocuments from '../manualDocuments/Page';
import AvaAppUserManagementNotice from '../userManagement/AvaAppUserManagementNotice';

//Auth
import CallbackPage from '../oidc/CallbackPage';

const log = new Logger('LOGGED_IN_ROUTER', LOG_LEVELS.DEBUG);

class LoggedInRouter extends Component {
  render() {
    log.debug('render(): ', this.props);
    const { featureFlags, complianceCalendarUrl } = getClientConfig();

    const resolveFeatureFlag = (flagKey) => featureFlags?.[flagKey] || this.props.featureFlags?.[flagKey];

    const enableLedgerReports = resolveFeatureFlag(FeatureFlagEnum.ENABLE_LEDGER_REPORTS);
    const enableManualDocuments = resolveFeatureFlag(FeatureFlagEnum.ENABLE_MANUAL_DOCUMENTS);
    const enableBulkActions = resolveFeatureFlag(FeatureFlagEnum.ENABLE_BULK_ACTIONS);
    const enableComplianceCalendarAsHomePage = resolveFeatureFlag(FeatureFlagEnum.ENABLE_COMPLIANCE_CALENDAR_AS_HOME_PAGE);
    const enableProcessedDocumentsPage = resolveFeatureFlag(FeatureFlagEnum.ENABLE_PROCESSED_DOCUMENTS_PAGE);
    const enableUploadedDocumentsPage = resolveFeatureFlag(FeatureFlagEnum.ENABLE_UPLOADED_DOCUMENTS_PAGE);
    const enableApiLicenseKeyPage = resolveFeatureFlag(FeatureFlagEnum.ENABLE_API_LICENSE_KEY_PAGE);

    const enableDynamicReports = featureFlags === undefined || featureFlags?.EnableDynamicReports;
    const enableTemplatesMenu = featureFlags === undefined || featureFlags?.EnableTemplatesMenu;
    const iamAuthEnabled = this.props.iamData?.isIamUser;
    const huliveProducts = [ProductEnum.LIVE_REPORTING];
    const vatProducts = [ProductEnum.VAT_REPORTING];
    const allProducts = [ProductEnum.VAT_REPORTING, ProductEnum.LIVE_REPORTING];

    const renderHomePage = () => {
      if (!this.props.featureFlagsLoaded) {
        return <BusySpinner busy={true} />;
      }
      if (enableComplianceCalendarAsHomePage && complianceCalendarUrl) {
        window.location.href = complianceCalendarUrl;
        return null;
      }
      return (
        <div>
          <Products show={huliveProducts}><Redirect to="/viewTransmissions" /></Products>
          <Products show={vatProducts}><Redirect to="/openReturns?defaultMonth=now&defaultYear=now" /></Products>
        </div>
      );
    };

    return (
      <div>
        <Switch>

          <Route exact path="/" render={renderHomePage} />
          <Route exact path="/index.html" render={renderHomePage} />
          <Route exact path="/dashboard" render={renderHomePage} />

          {/* Live Reporting*/}
          <AuthorizedRoute path="/setupLiveReporting" component={LiveReportingSetup} show={huliveProducts} />
          <AuthorizedRoute path="/viewTransmissions" component={ViewTransmissions} show={huliveProducts} />

     {/* Add and manage data */}
     <AuthorizedRoute path="/upload" component={Upload} show={vatProducts}/>
     {enableProcessedDocumentsPage ?
       <AuthorizedRoute path="/ProcessedDocuments" component={ProcessedDocuments} show={vatProducts} /> : null
     }
     {enableUploadedDocumentsPage ?
       <AuthorizedRoute path="/uploadedDocuments" component={UploadedDocuments} show={vatProducts} /> : null
     }
     <AuthorizedRoute path="/newUpload" component={NewUploadPage} show={vatProducts}/>
     {enableTemplatesMenu &&
      <AuthorizedRoute path="/templates" component={TemplatesPage} show={vatProducts}/>}

          {/* Errors and warnings */}
          <AuthorizedRoute path="/errorsAndWarnings" component={ErrorsAndWarnings} show={vatProducts} />
          <AuthorizedRoute path="/ErrorDocumentDetail/:errorId" component={ErrorDocumentDetail} show={vatProducts} />

          {/* Check and view */}
          <AuthorizedRoute path="/openReturns" component={OpenReturns} show={vatProducts} />
          <AuthorizedRoute exact path="/filing/:countryCode/:companyId/:filingTypeId/:dueDate/:startDate/:endDate/:filterMonth/:filterYear" component={FilingPage} show={vatProducts} />
          <AuthorizedRoute path="/closedReturns" component={ClosedReturns} show={vatProducts} />

          {/* Tools */}
          <AuthorizedRoute path="/TransactionReports" component={TransactionReports} show={vatProducts} />
          <AuthorizedRoute path="/VATCodeSimulator" component={VATCodeSimulator} show={vatProducts} />
          <AuthorizedRoute path="/VATCodes" component={VATCodes} show={vatProducts} />
          <AuthorizedRoute path="/VATCodeReport" component={VATCodeReport} show={vatProducts} />
          <AuthorizedRoute path="/MergeXmlFiles" component={MergeXmlFiles} show={vatProducts} />
          {enableLedgerReports &&
            <AuthorizedRoute path="/SalesAndPurchase" component={SalesAndPurchaseLedger} show={vatProducts} />
          }
          <AuthorizedRoute path="/CountriesOnHold" component={CountriesOnHold} show={vatProducts} />
          {/* Enable Dynamic reporting only if feature flag is set. If feature flags have not loaded yet, assume true to avoid unexpected behaviour */}
          {enableDynamicReports &&
            <AuthorizedRoute exact path="/DynamicReports" component={DynamicReportForm} show={vatProducts} />
          }
          {enableDynamicReports &&
            <AuthorizedRoute exact path="/DynamicReports/:category" component={DynamicReportForm} show={vatProducts} />
          }
          {enableDynamicReports &&
            <AuthorizedRoute exact path="/DynamicReports/:category/:report" component={DynamicReportForm} show={vatProducts} />
          }

          {enableManualDocuments &&
            <AuthorizedRoute path="/ManualDocuments" component={ManualDocuments} show={vatProducts} />
          }
          {/* Transaction Details */}
          <AuthorizedRoute path="/TransactionSearch" component={TransactionSearch} show={vatProducts} />
          <AuthorizedRoute path="/TransactionDetail/:docId/edit" component={TransactionEdit} show={vatProducts} />
          {enableBulkActions ?
            <AuthorizedRoute path="/BulkActions" component={BulkActions} show={vatProducts} /> : null
          }

          {/* Account */}
          <AuthorizedRoute path="/AccountSettings" component={AccountSettings} show={vatProducts} />
          {iamAuthEnabled &&
            <AuthorizedRoute path="/CompanyPermissions" component={CompanyPermissionsPage} show={vatProducts} />
          }
          {iamAuthEnabled &&
            <AuthorizedRoute path="/ViewCompanyPermissions" component={ViewCompanyPermissions} show={vatProducts} />
          }
          {iamAuthEnabled &&
            <AuthorizedRoute path="/EditCompanyPermissions" component={EditCompanyPermissions} show={vatProducts} />
          }
          {iamAuthEnabled &&
            <AuthorizedRoute path="/DirectSubmissionPermissions" component={DirectSubmissionPermissionsPage} show={vatProducts} />
          }
          {!iamAuthEnabled &&
            <AuthorizedRoute exact path="/AddUser" component={AddUserRouteGuard} show={vatProducts} />
          }
          {iamAuthEnabled ? (
              <AuthorizedRoute path="/UserManagement" component={AvaAppUserManagementNotice} show={vatProducts} />
            ) : (
              <AuthorizedRoute path="/UserManagement" component={UserManagementPage} show={vatProducts} />
            )
          }
          {!iamAuthEnabled &&
            <AuthorizedRoute exact path="/EditUser" component={EditUser} show={vatProducts} />
          }
          {!iamAuthEnabled &&
            <AuthorizedRoute exact path="/ViewUserDetails" component={ViewUserDetails} show={vatProducts} />
          }
          <AuthorizedRoute path="/Companies" component={Companies} show={vatProducts} />
          <AuthorizedRoute path="/VATGroups" component={VATGroups} show={vatProducts} />

          {/* Settings */}
          <AuthorizedRoute path="/ExchangeRates" component={ExchangeRates} show={vatProducts} />
          {enableApiLicenseKeyPage &&
            <AuthorizedRoute path="/apiLicenseKey" component={ApiLicenseKeyPage} show={vatProducts} />
          }

          {/* Auth */}
          <Route exact path="/auth/callback" component={CallbackPage} />

          {/* Other */}
          <AuthorizedRoute path="/notAllowed" component={NotAuthorized} show={allProducts} />
          <AuthorizedRoute component={NotFound} show={allProducts} />
        </Switch>

      </div>
    )
  }
  UNSAFE_componentWillMount() {
    log.debug('componentWillMount(): ', this.props);
    this.props.onPageLoad(this.props.displayLanguage || this.props.preferredLanguage, this.props.customerId);
  }
}

LoggedInRouter.defaultProps = {
};

// Which props do we want to inject, given the global state?
function mapStateToProps(state, props) {
  log.debug('mapStateToProps(): ', state, props);
  return {
    preferredLanguage: state.authentication && state.authentication.data && state.authentication.data.preferredLanguage,
    customerId: state.authentication && state.authentication.data && state.authentication.data.customerId,
    featureFlags: state.featureFlag && state.featureFlag.data,
    featureFlagsLoaded: !!(state.featureFlag && state.featureFlag.loaded),
    iamData: state.authentication && state.authentication.data && state.authentication.data.iamData
  };
}

function mapDispatchersToProps(dispatch) {
  return {
    onPageLoad: (displayLanguage, customerId) => {
      // This contains all the state that should be loaded on successful login,
      // which each page should be able to count on being present and not have to load itself.
      log.debug('onPageLoad(): ', displayLanguage, customerId);
      dispatch(LANGUAGES_CONTROLLER.doLoad(displayLanguage));
      dispatch(AUTHENTICATION_API.doGetLicences(customerId));
      dispatch(CONTENT_CONTROLLER.doLoad());
      dispatch(CONTENT_CONTROLLER.getContentData());
      dispatch(COMPANIES_CONTROLLER.doLoadCompaniesSummary());
    }
  }
}


// Wrap the component to inject dispatch and state into it
export default withRouter(connect(mapStateToProps, mapDispatchersToProps)(LoggedInRouter));
